import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Download, X } from 'lucide-react';

import { SUPPORTED_LANGS, DEFAULT_LANG } from '@/i18n';

/**
 * PWA install prompt — non-blocking banner inviting mobile visitors to add
 * Inside Panama to their home screen.
 *
 * Trigger rules :
 *   1. Increments `ip_visitCount` in localStorage on each mount; only shows
 *      from the 3rd visit onward (don't spam first-timers).
 *   2. Listens for the browser's `beforeinstallprompt` (Chrome / Edge /
 *      Samsung). On iOS Safari, no event fires — we show a static "Add via
 *      Share menu" hint if mobile + Safari is detected.
 *   3. Hidden if already running standalone (display-mode: standalone or
 *      navigator.standalone on iOS).
 *   4. Session-dismissed after the user clicks "Install", "Later", or X.
 *   5. Hard-dismissed (forever) if the user has accepted or declined the
 *      native prompt — we don't want to nag.
 *
 * Mounted globally in Layout.jsx; renders nothing on desktop.
 *
 * @example
 *   <PWAInstallPrompt />
 */
const VISIT_KEY = 'ip_visitCount';
const SESSION_DISMISS_KEY = 'ip_pwa_dismissed_session';
const HARD_DISMISS_KEY = 'ip_pwa_dismissed_forever';
const MIN_VISITS = 3;
const SHOW_DELAY_MS = 6000;

function isStandalone() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.navigator.standalone === true) return true; // iOS Safari
  } catch {
    /* ignore */
  }
  return false;
}

function isIOSMobileSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS && isSafari;
}

function isMobile() {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent || '');
}

export default function PWAInstallPrompt() {
  const { t: tns, i18n } = useTranslation('components');
  const reduce = useReducedMotion();
  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;
  void lang; // kept for potential future locale-specific behaviour
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState('native'); // 'native' | 'ios-hint'
  const deferredPromptRef = useRef(null);
  // Local alias so call sites stay short.
  const t = (key) => tns(`pwaInstall.${key}`);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStandalone()) return; // already installed → never bother
    if (!isMobile()) return; // desktop is fine without an install nag

    // Hard-dismiss (already accepted or declined a previous prompt)
    let hardDismissed = false;
    try { hardDismissed = localStorage.getItem(HARD_DISMISS_KEY) === '1'; } catch { /* */ }
    if (hardDismissed) return;

    // Session-dismiss (clicked X / Later this session)
    let sessionDismissed = false;
    try { sessionDismissed = sessionStorage.getItem(SESSION_DISMISS_KEY) === '1'; } catch { /* */ }
    if (sessionDismissed) return;

    // Visit count gate — only nag from 3rd visit
    let visits = 0;
    try { visits = Number(localStorage.getItem(VISIT_KEY) || 0); } catch { /* */ }
    visits += 1;
    try { localStorage.setItem(VISIT_KEY, String(visits)); } catch { /* */ }
    if (visits < MIN_VISITS) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setMode('native');
      setTimeout(() => setShow(true), SHOW_DELAY_MS);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari fallback — no beforeinstallprompt event, but we can still
    // show a static "tap share, then Add to Home Screen" hint.
    let iosTimer = null;
    if (isIOSMobileSafari()) {
      iosTimer = setTimeout(() => {
        if (deferredPromptRef.current) return; // a real prompt arrived first
        setMode('ios-hint');
        setShow(true);
      }, SHOW_DELAY_MS);
    }

    const onInstalled = () => {
      setShow(false);
      try { localStorage.setItem(HARD_DISMISS_KEY, '1'); } catch { /* */ }
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismissSession = () => {
    setShow(false);
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, '1'); } catch { /* */ }
  };

  const dismissForever = () => {
    setShow(false);
    try { localStorage.setItem(HARD_DISMISS_KEY, '1'); } catch { /* */ }
  };

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return;
    try {
      prompt.prompt();
      const choice = await prompt.userChoice;
      // Either outcome → don't nag again
      dismissForever();
      // Best-effort analytics hook (Sentry / GA may pick this up via window event)
      try {
        window.dispatchEvent(new CustomEvent('ip:pwa-install-result', {
          detail: { outcome: choice?.outcome || 'unknown' },
        }));
      } catch { /* */ }
    } catch {
      dismissSession();
    } finally {
      deferredPromptRef.current = null;
    }
  };

  if (typeof document === 'undefined') return null;

  // Body copy varies between native (Chrome/Edge) and iOS Safari hint mode.
  const body = mode === 'ios-hint' ? t('bodyIos') : t('bodyNative');

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          role="dialog"
          aria-label={t('title')}
          aria-live="polite"
          initial={reduce ? false : { opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={reduce ? undefined : { opacity: 0, y: 10, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          className="fixed bottom-20 right-4 z-[80] w-[min(360px,calc(100vw-2rem))] rounded-card border border-bordeaux-600/20 bg-cream-50 p-4 shadow-card-hover md:bottom-6 dark:border-bordeaux-400/30 dark:bg-zinc-900 dark:shadow-none"
        >
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bordeaux-600/10 text-bordeaux-700 dark:bg-bordeaux-400/20 dark:text-bordeaux-300">
              <Download size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-eyebrow text-bordeaux-700 dark:text-bordeaux-400">
                {t('eyebrow')}
              </p>
              <p className="mt-1 font-serif text-sm font-semibold leading-snug text-navy-900 dark:text-white">
                {t('title')}
              </p>
              <p className="mt-1 text-xs leading-snug text-gray-700 dark:text-white/60">
                {body}
              </p>
              <div className="mt-3 flex items-center gap-2">
                {mode === 'native' && (
                  <button
                    type="button"
                    onClick={handleInstall}
                    className="inline-flex items-center gap-1 rounded-btn bg-bordeaux-600 px-3 py-1.5 text-xs font-semibold text-cream-50 hover:bg-bordeaux-700"
                  >
                    <Download size={11} aria-hidden="true" />
                    {t('install')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={dismissSession}
                  className="text-[11px] text-gray-700 hover:text-navy-900 dark:text-white/60 dark:hover:text-white"
                >
                  {t('later')}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissSession}
              aria-label={t('dismiss')}
              className="-mr-1 -mt-1 rounded-btn p-1 text-gray-700 hover:bg-cream-100 hover:text-navy-900 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

